/* =========================================================
   DearRoot — shared theme JS
   countdown • mobile nav • cart engine + drawer + goals
   accordion • reveals • sticky ATC • toast • marquee
   ========================================================= */
(function () {
  'use strict';
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  // Format in the buyer's active (presentment) currency — Shopify converts the
  // amounts; we must not hardcode "$" or AED/EUR/etc. render with the wrong symbol.
  const _cfg = window.DR_CFG || {};
  let _nf = null;
  try {
    _nf = new Intl.NumberFormat(_cfg.locale || 'en', {
      style: 'currency', currency: _cfg.currency || 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 2
    });
  } catch (e) { _nf = null; }
  const money = n => _nf ? _nf.format(n) : '$' + (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, '');

  /* ---------- Cart (Shopify Ajax API) ---------- */
  const routes = window.routes || {};
  const GOAL_BASE = 50;                       // free-shipping threshold in the shop's base currency (USD); converted per market below
  const fmt = c => money((+c || 0) / 100);    // cents -> "$x.xx"

  // The demo cross-sell rail is disabled until real upsell variants are wired.
  // To enable, add entries like { variant: '123', title, meta, price (cents), compare, image }.
  const UPSELLS = [];

  let cartData = null;

  async function getCart() {
    const r = await fetch((routes.cart_url || '/cart') + '.js', { headers: { 'Accept': 'application/json' } });
    return r.json();
  }
  async function postCart(url, body) {
    const r = await fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.description || d.message || 'Cart error');
    return d;
  }

  window.DR = window.DR || {};
  // items: [{ id: variantId, quantity, selling_plan? }]
  window.DR.cartAdd = async function (items, opts) {
    opts = opts || {};
    try {
      await postCart(routes.cart_add_url || '/cart/add', { items });
      cartData = await getCart();
      renderCart(cartData);
      if (opts.open !== false) openDrawer();
      pulseCount();
      toast('Added to cart');
    } catch (e) {
      toast(e.message || 'Could not add to cart');
    }
  };
  window.DR.openDrawer = openDrawer;
  window.DR.refreshCart = () => getCart().then(c => { cartData = c; renderCart(c); });

  async function changeQty(key, quantity) {
    try {
      cartData = await postCart(routes.cart_change_url || '/cart/change', { id: key, quantity });
      renderCart(cartData);
      pulseCount();
    } catch (e) { toast('Could not update cart'); }
  }

  /* ---------- Subscription <-> one-time toggle ---------- */
  const SUB_PLAN = () => {
    const cfg = (window.DR_CFG && DR_CFG.subPlan) || null;
    const drawer = $('#cartDrawer');
    return cfg || (drawer && drawer.dataset.subPlan) || null;
  };
  // True if a given line currently carries a selling plan (is subscribed).
  function lineIsSubscribed(line) {
    return !!(line && line.selling_plan_allocation);
  }
  // Switch one line's selling plan. plan = id to subscribe, null to remove.
  // Primary path: /cart/change.js supports `selling_plan` (since 2022). Falls back
  // to remove + re-add by variant if the change is rejected.
  async function setLinePlan(line, plan) {
    const changeUrl = routes.cart_change_url || '/cart/change';
    try {
      await postCart(changeUrl, { id: line.key, quantity: line.quantity, selling_plan: plan || null });
    } catch (e) {
      // Fallback: drop the line, then re-add the same variant/qty with (or without) the plan.
      await postCart(changeUrl, { id: line.key, quantity: 0 });
      const item = { id: line.variant_id || line.id, quantity: line.quantity };
      if (plan) item.selling_plan = plan;
      await postCart(routes.cart_add_url || '/cart/add', { items: [item] });
    }
  }
  // Guards concurrent per-line plan switches (one in-flight at a time).
  let planBusy = false;
  // Flip ONE cart line between subscribe and one-time, then re-fetch + re-render
  // so just that line's price/benefit copy updates.
  async function setLineSellingPlan(line, subscribe) {
    if (planBusy) return;
    const plan = SUB_PLAN();
    if (!plan && subscribe) { toast('Subscription unavailable'); return; }
    planBusy = true;
    const row = document.querySelector('.line-plan[data-plan-key="' + line.key + '"]');
    if (row) row.classList.add('is-busy');
    try {
      await setLinePlan(line, subscribe ? String(plan) : null);
      cartData = await getCart();
      renderCart(cartData);
      pulseCount();
      toast(subscribe ? 'Switched to Subscribe & Save' : 'Switched to one-time');
    } catch (e) {
      toast('Could not update plan');
      cartData = await getCart(); renderCart(cartData);
    } finally {
      planBusy = false;
    }
  }

  // Per-line reassurance copy shown under a subscribed line.
  function lineBenefits(line) {
    return 'Cancel / Pause anytime';
  }

  /* ---------- Count badges ---------- */
  function updateCount(c) {
    const n = c ? c.item_count : 0;
    $$('.cart-count').forEach(el => { el.textContent = n; el.style.display = n ? 'grid' : 'none'; });
  }
  function pulseCount() {
    $$('.cart-count').forEach(el => { el.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.4)' }, { transform: 'scale(1)' }],
      { duration: 360, easing: 'ease-out' }); });
  }

  /* ---------- Render cart drawer (from the live Shopify cart) ---------- */
  function renderCart(c) {
    if (!c) { getCart().then(d => { cartData = d; renderCart(d); }); return; }
    cartData = c;
    updateCount(c);
    const body = $('#drawerBody'), foot = $('#drawerFoot'), goal = $('#cartGoal');
    if (!body) return;

    if (!c.items || !c.items.length) {
      const drawer = $('#cartDrawer');
      const shopUrl = (window.DR_CFG && DR_CFG.productUrl) || (drawer && drawer.dataset.productUrl) || '/collections/all';
      body.innerHTML =
        '<div class="drawer-empty">' +
          '<svg class="empty-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5.5 8h13l.9 10.4a2 2 0 0 1-2 2.1H6.6a2 2 0 0 1-2-2.1L5.5 8Z"/><path d="M8.7 8a3.3 3.3 0 0 1 6.6 0"/></svg>' +
          '<p class="empty-title">Your cart is empty</p>' +
          '<a class="btn btn--cta empty-cta" href="' + shopUrl + '">Continue shopping</a>' +
        '</div>';
      if (foot) foot.style.display = 'none';
      if (goal) goal.style.display = 'none';
      const upE = document.getElementById('cartUpsell'); if (upE) upE.hidden = true;
      return;
    }
    if (foot) foot.style.display = '';
    if (goal) goal.style.display = '';   // free-shipping bar stays pinned at top for any non-empty cart
    const upE = document.getElementById('cartUpsell'); if (upE) upE.hidden = UPSELLS.length === 0;

    const canSub = !!SUB_PLAN();   // only offer the switch when a selling plan is configured
    const subPct = Math.round((+(window.DR_CFG || {}).subOff || 0) * 100);
    body.innerHTML = c.items.map(l => {
      // Cadence (variant title) only — the subscribe state shows its own benefit line below.
      const vt = (l.variant_title && l.variant_title !== 'Default Title') ? l.variant_title : '';
      const lineWas = (l.original_line_price > l.final_line_price)
        ? `<span class="was">${fmt(l.original_line_price)}</span>` : '';
      const subbed = lineIsSubscribed(l);
      const planUi = canSub ? `
          <div class="line-plan${subbed ? ' on-sub' : ''}" data-plan-key="${l.key}">
            <div class="line-switch" role="group" aria-label="Purchase type">
              <button type="button" class="line-opt" data-line-mode="onetime" data-key="${l.key}" aria-pressed="${subbed ? 'false' : 'true'}">One-time</button>
              <button type="button" class="line-opt" data-line-mode="subscribe" data-key="${l.key}" aria-pressed="${subbed ? 'true' : 'false'}">Subscribe${subPct ? ` (Save ${subPct}%)` : ''}</button>
            </div>
            <div class="line-perks"${subbed ? '' : ' hidden'}>${subbed ? lineBenefits(l) : ''}</div>
          </div>` : '';
      return `
      <div class="cart-line">
        <div class="thumb">${l.image ? `<img src="${l.image}" alt="">` : ''}</div>
        <div>
          <div class="ti">${l.product_title}</div>
          ${vt ? `<div class="meta">${vt}</div>` : ''}
          <div class="qty" data-key="${l.key}">
            <button data-d="-1" aria-label="Decrease">&minus;</button>
            <span>${l.quantity}</span>
            <button data-d="1" aria-label="Increase">+</button>
          </div>
          ${planUi}
        </div>
        <div class="price">${fmt(l.final_line_price)}
          ${lineWas}
          <button class="rm" data-rmkey="${l.key}">Remove</button>
        </div>
      </div>`;
    }).join('');

    body.querySelectorAll('.qty').forEach(q => {
      const key = q.dataset.key;
      const line = c.items.find(it => String(it.key) === String(key));
      q.querySelectorAll('button').forEach(b =>
        b.addEventListener('click', () => changeQty(key, Math.max(0, (line ? line.quantity : 1) + (+b.dataset.d)))));
    });
    body.querySelectorAll('[data-rmkey]').forEach(b =>
      b.addEventListener('click', () => changeQty(b.dataset.rmkey, 0)));

    // Per-line subscribe <-> one-time switch: re-prices only that line via the Ajax API.
    body.querySelectorAll('.line-opt').forEach(b => b.addEventListener('click', () => {
      const subscribe = b.dataset.lineMode === 'subscribe';
      const line = c.items.find(it => String(it.key) === String(b.dataset.key));
      if (!line) return;
      if (subscribe === lineIsSubscribed(line)) return;   // already in this mode
      setLineSellingPlan(line, subscribe);
    }));

    // totals (all in cents)
    foot.querySelector('.tot').textContent = fmt(c.total_price);
    const cEl = foot.querySelector('.sub-count'); if (cEl) cEl.textContent = `(${c.item_count} ${c.item_count === 1 ? 'item' : 'items'})`;
    const wEl = foot.querySelector('.tot-was');
    if (wEl) { if (c.original_total_price > c.total_price) { wEl.textContent = fmt(c.original_total_price); wEl.style.display = ''; } else wEl.style.display = 'none'; }
    const saveNote = foot.querySelector('.save-note');
    const saved = c.original_total_price - c.total_price;
    if (saveNote) saveNote.textContent = saved > 0 ? `You're saving ${fmt(saved)}` : '';

    renderGoal(c.total_price);
  }

  /* ---------- In-cart upsell slider ---------- */
  function renderUpsell() {
    const track = document.getElementById('upTrack'); if (!track) return;
    if (!UPSELLS.length) { const upE = document.getElementById('cartUpsell'); if (upE) upE.hidden = true; return; }
    track.innerHTML = UPSELLS.map(u => `
      <div class="up-card">
        <div class="up-thumb">${u.image ? `<img src="${u.image}" alt="" onerror="this.style.display='none'">` : ''}</div>
        <div class="up-info">
          <div class="up-name">${u.title}</div>
          <div class="up-meta">${u.meta || ''}</div>
          <div class="up-price">${fmt(u.price)}${u.compare ? ` <span class="up-was">${fmt(u.compare)}</span>` : ''}</div>
        </div>
        <button class="up-add btn btn--ghost" type="button" data-up="${u.variant}">Add</button>
      </div>`).join('');
    track.querySelectorAll('[data-up]').forEach(b => b.addEventListener('click', () =>
      DR.cartAdd([{ id: b.dataset.up, quantity: 1 }])));
    const prev = $('.up-prev'), next = $('.up-next');
    const step = () => track.clientWidth;
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  }

  function renderGoal(totalCents) {
    const goal = $('#cartGoal'); if (!goal) return;
    const tot = (+totalCents || 0) / 100;
    // Convert the base-currency threshold into the buyer's market currency (Shopify.currency.rate).
    const rate = (window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate)) || 1;
    const max = GOAL_BASE * rate;
    const pct = Math.min(100, (tot / max) * 100);
    goal.querySelector('.gbar > i').style.width = pct + '%';
    const txt = goal.querySelector('.gtext');
    if (tot < max) txt.innerHTML = `Add <b>${fmt((max - tot) * 100)}</b> to unlock <b>Free Shipping</b>`;
    else txt.innerHTML = `<b>Free Shipping unlocked</b> — your order ships free.`;
  }

  /* ---------- Drawer open/close ---------- */
  function openDrawer() { const d = $('#cartDrawer'); if (d) { d.classList.add('open'); document.body.style.overflow = 'hidden'; } }
  function closeDrawer() { const d = $('#cartDrawer'); if (d) { d.classList.remove('open'); document.body.style.overflow = ''; } }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(msg) {
    let t = $('#toast');
    if (!t) {
      t = document.createElement('div'); t.id = 'toast'; t.className = 'toast';
      t.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M20 6 9 17l-5-5"/></svg><span></span>';
      document.body.appendChild(t);
    }
    t.querySelector('span').textContent = msg;
    t.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('show'), 2200);
  }
  window.DR.toast = toast;

  /* ---------- Countdown (rolling 2h) ---------- */
  function initCountdown() {
    const els = $$('[data-countdown]'); if (!els.length) return;
    const KEY = 'dr_cd_end';
    let end = +localStorage.getItem(KEY);
    if (!end || end < Date.now()) { end = Date.now() + 2 * 3600 * 1000; localStorage.setItem(KEY, end); }
    function tick() {
      let s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (s <= 0) { end = Date.now() + 2 * 3600 * 1000; localStorage.setItem(KEY, end); s = 7200; }
      const h = String(Math.floor(s / 3600)).padStart(2, '0');
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
      const sec = String(s % 60).padStart(2, '0');
      els.forEach(el => {
        const H = el.querySelector('[data-h]'), M = el.querySelector('[data-m]'), S = el.querySelector('[data-s]');
        if (H) H.textContent = h; if (M) M.textContent = m; if (S) S.textContent = sec;
      });
    }
    tick(); setInterval(tick, 1000);
  }

  /* ---------- Accordion ---------- */
  function initAccordion() {
    $$('.acc-item').forEach(item => {
      const q = item.querySelector('.acc-q'), a = item.querySelector('.acc-a');
      if (!q || !a) return;
      q.addEventListener('click', () => {
        const open = item.classList.contains('open');
        // optional: close siblings — keep independent here
        item.classList.toggle('open');
        a.style.maxHeight = open ? '0px' : a.scrollHeight + 'px';
      });
    });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const els = $$('.reveal'); if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(e => e.classList.add('in')); return; }
    const io = new IntersectionObserver((ents) => {
      ents.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(e => io.observe(e));
    // reveal anything already above the fold on the first frame (no observer wait)
    requestAnimationFrame(() => {
      els.forEach(e => { if (e.getBoundingClientRect().top < window.innerHeight * 0.92) e.classList.add('in'); });
    });
  }

  /* ---------- Mobile nav ---------- */
  function initMobileNav() {
    const mn = $('#mobileNav'); if (!mn) return;
    $$('[data-open-nav]').forEach(b => b.addEventListener('click', () => { mn.classList.add('open'); document.body.style.overflow = 'hidden'; }));
    mn.querySelectorAll('[data-close-nav], .scrim, a').forEach(b =>
      b.addEventListener('click', () => { mn.classList.remove('open'); document.body.style.overflow = ''; }));
  }

  /* ---------- Sticky ATC visibility ---------- */
  function initStickyATC() {
    const bar = $('#stickyAtc'); if (!bar) return;
    const anchor = $('#buyAnchor');
    // The bar shows once the buy box has scrolled out of view, but must retreat before it
    // can overlay the footer's legal/policy links at the very bottom of the page.
    const foot = $('.foot-bottom') || $('.site-foot');
    let pastAnchor = false, footerNear = false;
    const sync = () => bar.classList.toggle('show', pastAnchor && !footerNear);
    if (anchor) {
      new IntersectionObserver((ents) => {
        ents.forEach(e => { pastAnchor = !e.isIntersecting && e.boundingClientRect.top < 0; });
        sync();
      }, { threshold: 0 }).observe(anchor);
    }
    if (foot) {
      // rootMargin extends the viewport downward so the bar hides just before the footer reaches it.
      new IntersectionObserver((ents) => {
        ents.forEach(e => { footerNear = e.isIntersecting; });
        sync();
      }, { threshold: 0, rootMargin: '0px 0px 90px 0px' }).observe(foot);
    }
  }

  /* ---------- Marquee duplication ---------- */
  function initMarquee() {
    $$('.marquee-track').forEach(tr => { tr.innerHTML += tr.innerHTML; });
  }

  /* ---------- Announcement rotator ---------- */
  function initAnnounce() {
    const rot = $('[data-announce]'); if (!rot) return;
    const msgs = $$('.ann-msg', rot); if (msgs.length < 2) return;
    let i = 0;
    setInterval(() => {
      msgs[i].classList.remove('on');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('on');
    }, 4200);
  }

  /* ---------- Country / region selector ---------- */
  function initRegion() {
    const regions = $$('[data-region]'); if (!regions.length) return;
    regions.forEach(r => {
      const btn = r.querySelector('.region-btn');
      const form = r.querySelector('form');
      const input = r.querySelector('input[name="country_code"]');
      if (btn) btn.addEventListener('click', e => {
        e.stopPropagation();
        const open = r.classList.toggle('open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      r.querySelectorAll('[role="option"]').forEach(li => {
        const pick = () => {
          // Submit the Shopify localization form -> switches country/currency, reloads.
          if (input && li.dataset.value) { input.value = li.dataset.value; if (form) form.submit(); }
        };
        li.addEventListener('click', pick);
        li.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
      });
    });
    document.addEventListener('click', () => {
      regions.forEach(r => { r.classList.remove('open'); const b = r.querySelector('.region-btn'); if (b) b.setAttribute('aria-expanded', 'false'); });
    });
  }

  /* ---------- Star injection ---------- */
  function initStars() {
    const STAR = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.2l2.95 5.98 6.6.96-4.78 4.66 1.13 6.57L12 17.27 6.1 20.37l1.13-6.57L2.45 9.14l6.6-.96L12 2.2z"/></svg>';
    $$('[data-stars]').forEach(el => {
      const n = Math.round(parseFloat(el.dataset.stars || '5'));
      el.innerHTML = STAR.repeat(Math.max(0, Math.min(5, n)));
      el.setAttribute('role', 'img');
    });
  }

  /* ---------- Drawer wiring ---------- */
  function initDrawer() {
    $$('[data-open-cart]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); openDrawer(); }));
    const d = $('#cartDrawer');
    if (d) d.querySelectorAll('[data-close-cart], .drawer-scrim').forEach(b => b.addEventListener('click', closeDrawer));
    // Per-line subscribe <-> one-time switches are wired in renderCart (rebuilt each render).
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });
  }

  /* ---------- init ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    initMarquee(); initCountdown(); initAccordion(); initReveal();
    initMobileNav(); initDrawer(); initStickyATC(); initStars(); renderCart();
    initAnnounce(); initRegion(); renderUpsell();
  });
})();
